// Must stay the first import — polyfills before any consumer evaluates.
import '@workpay/ds-shared/utils/polyfills';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { queryClient } from '@lib/queryClient';
import { DsExternalProvider } from '@providers/DsExternalProvider';
import { useLocalizationStore } from '@stores/localizationStore';
import { getStringBundle } from '@services/localizationService';
import { getDirection } from '@workpay/ds-shared/utils/direction';
import { injectBrandFonts } from '@lib/fonts';
import { AppErrorScreen } from '@components/AppErrorScreen/AppErrorScreen';
import { routeTree } from './routeTree.gen';
import '@styles/global.scss';

// Child routes without their own errorComponent fall back to this; without it
// TanStack Router paints its bare "Something went wrong!" page.
const router = createRouter({ routeTree, defaultErrorComponent: AppErrorScreen });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Pre-warm the localization bundle so the first render has translations.
// Not awaited — render proceeds with English fallbacks if the fetch is slow.
const initialLanguage = useLocalizationStore.getState().language;
void queryClient.prefetchQuery({
  queryKey: ['localization', 'bundle', initialLanguage],
  queryFn: () => getStringBundle(initialLanguage),
  staleTime: Infinity,
});

// Apply <html dir> + <html lang> from current language. Runs before React
// mounts so the first paint already has the correct direction (no LTR
// flash for RTL users), and on every store change via subscribe.
function applyDirection(language: string) {
  const dir = getDirection(language);
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', language);
}

injectBrandFonts();
applyDirection(initialLanguage);
useLocalizationStore.subscribe((state) => applyDirection(state.language));

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <DsExternalProvider>
        <RouterProvider router={router} />
      </DsExternalProvider>
    </QueryClientProvider>
  </StrictMode>,
);
